import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ArtifactVersionAppliedTemplateRead, ArtifactTemplateSelectionRead } from '../schemas'

export type ArtifactTemplatesState = {
  // A draft's sticky template choice. Key = artifact_id → template_user_file_id.
  // An absent key means "no selection" — clearing deletes the key (no tombstone),
  // since the server is the source of truth and there are no optimistic writes.
  templateSelectionByArtifactId: Partial<Record<string, string>>
  // The template applied to a given version + its rendered output. Key = artifact_version_id.
  // Presence lets download/save serve the rendered output directly instead of re-applying.
  appliedTemplateByVersionId: Partial<Record<string, ArtifactVersionAppliedTemplateRead>>
}

const initialState: ArtifactTemplatesState = {
  templateSelectionByArtifactId: {},
  appliedTemplateByVersionId: {},
}

/**
 * Artifact Templates Slice
 * Holds persisted template state for drafts: the sticky per-draft selection and the
 * per-version applied record. Hydrated on conversation load and kept in sync from the
 * confirmed responses of select/clear/apply.
 */
export const artifactTemplatesSlice = createSlice({
  name: 'artifactTemplatesState',
  initialState,
  reducers: {
    nullifyData: () => initialState,

    // Hydration from conversation load. Assign per key so untouched entries keep their
    // identity and don't wake unrelated subscribers.
    upsertTemplateSelections: (state, action: PayloadAction<ArtifactTemplateSelectionRead[]>) => {
      for (const selection of action.payload) {
        state.templateSelectionByArtifactId[selection.artifact_id] = selection.template_user_file_id
      }
    },
    upsertAppliedTemplates: (state, action: PayloadAction<ArtifactVersionAppliedTemplateRead[]>) => {
      for (const applied of action.payload) {
        state.appliedTemplateByVersionId[applied.artifact_version_id] = applied
      }
    },

    // Single-entry mutations after a confirmed PUT / DELETE / apply.
    setTemplateSelection: (state, action: PayloadAction<{ artifactId: string; templateUserFileId: string }>) => {
      state.templateSelectionByArtifactId[action.payload.artifactId] = action.payload.templateUserFileId
    },
    clearTemplateSelection: (state, action: PayloadAction<{ artifactId: string }>) => {
      delete state.templateSelectionByArtifactId[action.payload.artifactId]
    },
  },
})

export const ArtifactTemplatesActions = artifactTemplatesSlice.actions

export default artifactTemplatesSlice.reducer
